import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Pagination,
  Drawer,
  Fab,
  useTheme,
  useMediaQuery,
  Badge,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Stack,
  Chip,
} from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import SupportAgent from '@mui/icons-material/SupportAgent';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import AIIcon from '@mui/icons-material/AutoAwesome';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import Cart from '../components/market/Cart';
import MarketplaceHero from '../components/market/MarketplaceHero';
import PromotionBanner from '../components/market/PromotionBanner';
import ProductFilters from '../components/market/ProductFilters';
import ProductCardEnhanced from '../components/market/ProductCardEnhanced';
import FeaturedSection from '../components/market/FeaturedSection';
import ProductQuickView from '../components/market/ProductQuickView';
import { Product } from '../data/mockMarketplace';
import { useProducts } from '../context/ProductContext';
import DataService from '../services/dataService';
import { PageTransition, StaggeredReveal } from '../components/common/PageTransition';

const Marketplace: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { cartItems, addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { products, refreshProducts } = useProducts();

  // State
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Filters State
  const [priceRange, setPriceRange] = useState<number[]>([0, 20000]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [minRating, setMinRating] = useState<number | null>(null);

  // Constants
  const ITEMS_PER_PAGE = 9;

  // Simulated AI & Social Proof Data
  const productsWithIntelligence = useMemo(() => {
    return products.map((p, index) => ({
      ...p,
      isTrending: index % 3 === 0,
      recentViews: Math.floor(Math.random() * 50) + 10,
      aiMatchScore: Math.floor(Math.random() * 20) + 80, // 80-100% match
    }));
  }, [products]);

  // Filter Logic
  useEffect(() => {
    let result = productsWithIntelligence;

    // Filter by Category
    if (selectedCategories.length > 0) {
      result = result.filter((p) => selectedCategories.includes(p.category) || selectedCategories.some(c => p.category.toLowerCase().includes(c.toLowerCase())));
    }

    // Filter by Price
    result = result.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Filter by Rating
    if (minRating) {
      result = result.filter((p) => p.rating >= minRating);
    }

    setFilteredProducts(result as any);
    setPage(1); // Reset to first page on filter change
  }, [productsWithIntelligence, selectedCategories, priceRange, minRating]);

  // Pagination Logic
  const paginatedProducts = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, page]);

  // Handlers
  const handleAddToCart = async (product: Product) => {
    try {
      await addToCart(product);
      setSnackbar({ open: true, message: `${product.name} added to cart!`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to add item to cart', severity: 'error' });
    }
  };

  const handleClearFilters = () => {
    setPriceRange([0, 20000]);
    setSelectedCategories([]);
    setMinRating(null);
  };

  const { user } = useAuth();

  const handleRemoveFromMarketplace = async (product: Product) => {
    try {
      if (!window.confirm('Remove this product from the marketplace?')) return;
      if (!user) {
        setSnackbar({ open: true, message: 'You must be logged in to remove products.', severity: 'error' });
        return;
      }
      const productOwnerId = (product as any).user_id;
      const isOwner = productOwnerId && user.id === productOwnerId;
      const isAdmin = user.role === 'admin';

      if (!isAdmin && !isOwner) {
        setSnackbar({ open: true, message: 'Only the lister or an admin can remove this product.', severity: 'error' });
        return;
      }
      await DataService.deleteMarketplaceProduct(product.id);
      await refreshProducts();
      if (isAdmin && !isOwner) {
        setSnackbar({ open: true, message: 'Product removed from marketplace by admin.', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Product removed from marketplace.', severity: 'success' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.message || 'Failed to remove product', severity: 'error' });
    }
  };

  return (
    <PageTransition>
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 8 }}>
        <StaggeredReveal delay={0.1}>
          <PromotionBanner />
        </StaggeredReveal>
        
        <Container maxWidth="xl" sx={{ pt: 4 }}>
          {/* Header Actions (Cart) */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => setCartOpen(true)} color="primary">
              <Badge badgeContent={cartItems.reduce((acc, item) => acc + item.quantity, 0)} color="error">
                <ShoppingCart />
              </Badge>
            </IconButton>
          </Box>

          {/* Hero Section */}
          <StaggeredReveal delay={0.2}>
            <MarketplaceHero />
          </StaggeredReveal>

          {/* AI Recommendations Section */}
          <Box sx={{ mb: 6 }}>
            <StaggeredReveal delay={0.3}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <AIIcon color="primary" />
                <Typography variant="h5" fontWeight="bold">Personalized for You</Typography>
                <Chip label="AI Powered" size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Stack>
            </StaggeredReveal>
            <Grid container spacing={3}>
              {productsWithIntelligence.slice(0, 3).map((product, index) => (
                <Grid key={`ai-${product.id}`} item xs={12} sm={4}>
                  <StaggeredReveal delay={0.4 + index * 0.1}>
                    <ProductCardEnhanced
                      product={product as any}
                      onAddToCart={handleAddToCart}
                      onToggleFavorite={() => toggleWishlist(product as any)}
                      isFavorite={isInWishlist(product.id)}
                      onQuickView={setQuickViewProduct}
                    />
                  </StaggeredReveal>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Best Sellers Section */}
          <StaggeredReveal delay={0.7}>
            <FeaturedSection
              title="Best Sellers"
              products={products.filter(p => p.isBestSeller)}
              onAddToCart={handleAddToCart}
              onToggleFavorite={toggleWishlist}
              isInWishlist={isInWishlist}
              onQuickView={setQuickViewProduct}
              viewAllLink="#"
            />
          </StaggeredReveal>

          <Grid container spacing={4} sx={{ mt: 2 }}>
            {/* Sidebar Filters - Desktop */}
            {!isMobile && (
              <Grid item md={3} lg={2.5}>
                <StaggeredReveal delay={0.8}>
                  <Box sx={{ position: 'sticky', top: 24 }}>
                    <ProductFilters
                      priceRange={priceRange}
                      setPriceRange={setPriceRange}
                      selectedCategories={selectedCategories}
                      setSelectedCategories={setSelectedCategories}
                      minRating={minRating}
                      setMinRating={setMinRating}
                      onClear={handleClearFilters}
                    />
                  </Box>
                </StaggeredReveal>
              </Grid>
            )}

            {/* Main Content */}
            <Grid item xs={12} md={9} lg={9.5}>
              <StaggeredReveal delay={0.9}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" fontWeight="bold">
                    All Products ({filteredProducts.length})
                  </Typography>

                  {isMobile && (
                    <Button
                      startIcon={<FilterList />}
                      variant="outlined"
                      onClick={() => setMobileOpen(true)}
                    >
                      Filters
                    </Button>
                  )}
                </Box>
              </StaggeredReveal>

              {filteredProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No products found matching your criteria.
                  </Typography>
                  <Button sx={{ mt: 2 }} onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {paginatedProducts.map((product, index) => {
                    const productOwnerId = (product as any).user_id;
                    const isOwner = user && productOwnerId && user.id === productOwnerId;
                    const isAdmin = user && user.role === 'admin';

                    return (
                      <Grid key={product.id} item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
                        <StaggeredReveal delay={1.0 + (index % 3) * 0.1}>
                          <Box sx={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <ProductCardEnhanced
                              product={product}
                              onAddToCart={handleAddToCart}
                              onToggleFavorite={() => toggleWishlist(product)}
                              isFavorite={isInWishlist(product.id)}
                              onQuickView={setQuickViewProduct}
                            />
                            {/* ... owner/admin badges ... */}
                          </Box>
                        </StaggeredReveal>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              {/* ... pagination ... */}
            </Grid>
          </Grid>
        </Container>
        {/* ... drawers/snackbars ... */}
      </Box>
    </PageTransition>
  );
};

export default Marketplace;
